class CollaborativeLockManager {
    constructor(websocket, userId) {
        this.ws = websocket;
        this.userId = userId;
        this.locks = new Map();  // sectionId -> {userId, userName, lockedAt}
        this.myLocks = new Set();  // sections I currently hold
        this.LOCK_TIMEOUT = 30000;  // 30 seconds local timeout check
        this.init();
    }

    init() {
        // Setup lock expiration checker
        this.lockCheckInterval = setInterval(() => {
            this.checkLockExpiration();
        }, 5000);
    }

    requestLock(sectionId, sectionName) {
        // Check if already locked by someone else
        const existingLock = this.locks.get(sectionId);
        if (existingLock && existingLock.userId !== this.userId) {
            this.showLockNotification(existingLock.userName, sectionName);
            return false;
        }

        // Request lock from server
        if (this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'lock_request',
                section_id: sectionId,
                section_name: sectionName
            }));
        }

        return true;
    }

    releaseLock(sectionId) {
        if (!this.myLocks.has(sectionId)) {
            return;
        }

        if (this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'lock_release',
                section_id: sectionId
            }));
        }

        this.myLocks.delete(sectionId);
        this.locks.delete(sectionId);
        this.removeLockIndicator(sectionId);
    }

    // Server Message Handlers

    onLockGranted(sectionId, holderId, holderName) {
        console.log(`🔒 Lock granted for ${sectionId}`);
        const isMyLock = holderId === this.userId;

        if (isMyLock) {
            this.myLocks.add(sectionId);
        }

        this.locks.set(sectionId, {
            userId: holderId || this.userId,
            userName: isMyLock ? 'You' : holderName,
            lockedAt: Date.now()
        });

        // Apply visual lock indicator
        this.applyLockIndicator(sectionId, isMyLock, holderName);

        // Dispatch event so other components know (e.g. StateSynchronizer)
        document.dispatchEvent(new CustomEvent('lockChanged', {
            detail: { sectionId, isLocked: true, isMyLock }
        }));

        if (isMyLock) {
            Utils?.notify?.('You now have control of the view', 'success');
        }
    }

    onLockDenied(sectionId, reason) {
        console.log(`🔒 Lock denied for ${sectionId}: ${reason}`);
        Utils?.notify?.(reason || 'Cannot take control right now', 'warning');
    }

    onLockReleased(sectionId, userId) {
        console.log(`🔒 Lock released for ${sectionId}`);
        this.locks.delete(sectionId);
        if (userId === this.userId) {
            this.myLocks.delete(sectionId);
        }
        this.removeLockIndicator(sectionId);

        document.dispatchEvent(new CustomEvent('lockChanged', {
            detail: { sectionId, isLocked: false }
        }));
    }

    onLockTaken(sectionId, userId, userName) {
        console.log(`🔒 Lock taken by ${userName} for ${sectionId}`);
        this.locks.set(sectionId, {
            userId: userId,
            userName: userName,
            lockedAt: Date.now()
        });

        this.applyLockIndicator(sectionId, false, userName);

        document.dispatchEvent(new CustomEvent('lockChanged', {
            detail: { sectionId, isLocked: true, isMyLock: false, holder: userName }
        }));

        Utils?.notify?.(`${userName} now has control of the view`, 'info');
    }

    hasLock(sectionId) {
        return this.myLocks.has(sectionId);
    }

    isLockedByOther(sectionId) {
        const lock = this.locks.get(sectionId);
        return lock && lock.userId !== this.userId;
    }

    onRemoteLockAcquired(sectionId, userId, userName, sectionName) {
        this.locks.set(sectionId, {
            userId: userId,
            userName: userName,
            lockedAt: Date.now()
        });

        this.applyLockIndicator(sectionId, false, userName);

        document.dispatchEvent(new CustomEvent('lockChanged', {
            detail: { sectionId, isLocked: true, isMyLock: false, holder: userName }
        }));
    }

    onRemoteLockReleased(sectionId, userId) {
        this.locks.delete(sectionId);
        this.removeLockIndicator(sectionId);

        document.dispatchEvent(new CustomEvent('lockChanged', {
            detail: { sectionId, isLocked: false }
        }));
    }

    // UI Helpers

    applyLockIndicator(sectionId, isMyLock, userName = null) {
        // Special handling for 'camera' which isn't a DOM element but a concept
        if (sectionId === 'camera') {
            this.updateCameraControlIndicator(isMyLock, userName);
            return;
        }

        const element = document.getElementById(sectionId);
        if (!element) return;

        // Remove existing lock overlay
        const existingOverlay = element.querySelector('.lock-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        if (isMyLock) {
            element.classList.add('section-locked-by-me');
        } else {
            const overlay = document.createElement('div');
            overlay.className = 'lock-overlay';
            overlay.innerHTML = `
                <div class="lock-message">
                    🔒 Edited by ${this.escapeHtml(userName)}
                </div>
            `;
            element.appendChild(overlay);
            element.classList.add('section-locked-by-other');
        }
    }

    updateCameraControlIndicator(isMyLock, userName) {
        const btn = document.getElementById('btnTakeControl');
        if (!btn) return;

        if (isMyLock) {
            btn.classList.add('active');
            btn.innerHTML = '🔒 You have control';
            btn.disabled = false;
        } else if (userName) {
            btn.classList.remove('active');
            btn.innerHTML = `🔒 ${this.escapeHtml(userName)} has control`;
            btn.disabled = true;
        } else {
            // Unlocked
            btn.classList.remove('active');
            btn.innerHTML = '🎮 Take Control';
            btn.disabled = false;
        }
    }

    removeLockIndicator(sectionId) {
        if (sectionId === 'camera') {
            this.updateCameraControlIndicator(false, null);
            return;
        }

        const element = document.getElementById(sectionId);
        if (!element) return;

        const overlay = element.querySelector('.lock-overlay');
        if (overlay) {
            overlay.remove();
        }

        element.classList.remove('section-locked-by-me');
        element.classList.remove('section-locked-by-other');
    }

    showLockNotification(userName, sectionName) {
        // Simple alert/toast
        const msg = `${userName} is currently editing ${sectionName || 'this section'}`;
        // Implement a toast notification here if one exists, otherwise console/alert
        console.log(msg);
        alert(msg); // Fallback
    }

    checkLockExpiration() {
        // cleanup stale locks if needed
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}
